/**
 * 대화 단독 내보내기/가져오기 오케스트레이션 — 결정 #6. 한 대화를 그에 딸린
 * 캐릭터·로어북·메시지와 함께 JSON 번들로 묶거나 풀어 저장한다.
 *
 * core(conversation-io)가 ID 재매핑을 책임지고, 여기서는 저장소에서 엔티티를
 * 모으고(export) / 재매핑된 엔티티를 저장한다(import). storage 포트만 다룬다(#10).
 */
import {
  buildConversationBundle,
  exportConversationToJson,
  importConversationFromJson,
  type Character,
  type Lorebook,
  type Message,
} from "@roqeet/core";
import { storage } from "./storage.js";

/** liveMessages를 1회 읽어 현재 메시지 배열을 얻는다. */
async function readMessagesOnce(chatId: string): Promise<Message[]> {
  const live = storage.liveMessages(chatId);
  return new Promise<Message[]>((resolve) => {
    const unsub = live.subscribe((v) => {
      resolve(v);
      queueMicrotask(unsub);
    });
  });
}

/** 대화를 번들 JSON으로 내보내 파일로 다운로드한다(결정 #6). */
export async function exportConversation(chatId: string): Promise<void> {
  const chat = await storage.chats.get(chatId);
  if (!chat) throw new Error("대화를 찾을 수 없습니다.");
  const messages = await readMessagesOnce(chatId);

  // 참가자/메시지가 참조하는 캐릭터 + 캐릭터/채팅 로어북을 모은다.
  const charIds = new Set<string>();
  for (const p of chat.participants) if (p.characterId) charIds.add(p.characterId);
  for (const m of messages) if (m.characterId) charIds.add(m.characterId);
  const characters = (await storage.characters.getMany([...charIds])).filter(
    (c): c is Character => !!c,
  );

  const lbIds = new Set<string>(chat.lorebookIds);
  for (const c of characters) if (c.lorebookId) lbIds.add(c.lorebookId);
  const lorebooks = (await storage.lorebooks.getMany([...lbIds])).filter(
    (l): l is Lorebook => !!l,
  );

  const bundle = buildConversationBundle(chat, messages, characters, lorebooks);
  const json = exportConversationToJson(bundle);

  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safe = chat.title.replace(/[^\w가-힣\- ]/g, "_").slice(0, 60) || "conversation";
  a.href = url;
  a.download = `${safe}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 번들 파일에서 대화를 가져와 저장한다(결정 #6). core가 모든 id를 재매핑하므로
 * 기존 데이터와 충돌하지 않는다. 가져온 새 chatId를 반환한다.
 */
export async function importConversation(file: Blob): Promise<string> {
  const text = await file.text();
  const result = importConversationFromJson(text);

  // 참조 순서: 로어북 → 캐릭터 → 메시지 → 대화(역참조가 없도록).
  if (result.lorebooks.length) await storage.lorebooks.putMany(result.lorebooks);
  if (result.characters.length) await storage.characters.putMany(result.characters);
  if (result.messages.length) await storage.putMessages(result.messages);
  await storage.chats.put({ ...result.chat, lastMessageAt: Date.now() });

  return result.chat.id;
}
